import React from 'react';

import {useSnackbar} from 'notistack';
import {Box, Typography, Link as MuiLink} from '@material-ui/core';

import {Utils} from 'commons';
import {MainContent} from 'layouts/commons';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import {ClientService, DocumentServices} from 'services';
import FrameworkForm from './regulatory-form/FrameworkForm';
import {getGeolocation, modulesValidation, nextStep} from 'commons/utils/Functions';
import {
    SnackbarVariants,
    Messages,
    MODULES,
    COMPLEMENT_MODULES,
    AnalyticsCategories,
    HistoryRoutes,
    Constants,
} from 'commons/constants';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {ClientContactServiceRequest, EfeResponse, Field} from 'commons/models';
import {useHistory} from 'react-router-dom';
import {useUserContext} from 'context/user/UserContext';
import {useAnalytics} from 'hooks/AnalyticsHook';
import {messageFunctions} from 'commons/utils';
import {GeolocationInfo, MailAuthResponse, OtpValidation} from 'commons/types/Types';
import {BulletStyles} from 'assets/tss/layouts/components';
import {EfeModal, EfeModalMsg, EfeOtp} from 'components';

function RegulatoryFramework() {
    const {
        personType,
        updateMenu,
        layout,
        expedientId,
        menu,
        fatherKycId,
        setExpedientId,
        setKycId,
        setFatherKycId,
        foreign,
        geolocationData,
        setGeolocationData,
    } = useMenuContext();
    const {
        showAlert,
        defaultError,
        genericError,
        showAlertError,
        closeAlert,
        actions,
    } = useAlert();
    const history = useHistory();
    const classes = BulletStyles();
    const {flow} = useFlowContext();
    const {user} = useUserContext();
    const {enqueueSnackbar} = useSnackbar();
    const {sendPageView, sendEvent} = useAnalytics();

    const fields: Array<Field> = useFlowFieldSearch();
    const [enrollment, setEnrollment] = React.useState<any>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<boolean>(false);
    const [token, setToken] = React.useState<boolean>(false);
    const [valuesToSubmit, setValuesToSubmit] = React.useState<ClientContactServiceRequest>();

    React.useEffect(() => {
        sendPageView(HistoryRoutes.REGULATORY_FRAMEWORK);
        if (fatherKycId > 0) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<ClientContactServiceRequest> =
                        await ClientService.getClientContact(user.username);
                    if (response && response.data) {
                        setEnrollment(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setEnrollment(undefined);
                }
            })();
        }
        updateMenu([...modulesValidation([...MODULES], layout), ...COMPLEMENT_MODULES]);
    }, []);

    async function onDownload(doc: string) {
        try {
            setIsLoading(true);
            const response = await DocumentServices.getDocument(doc);
            Utils.functions.downloadDocs(response, `${doc}.pdf`);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                variant: SnackbarVariants.SUCCESS,
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    async function saveGeolocation(expedientId: number) {
        let geolocationInfo: GeolocationInfo = {
            latitude: null,
            longitude: null,
        };
        if (!geolocationData.latitude || null === geolocationData.latitude) {
            geolocationInfo = await getGeolocation();
            if (null === geolocationInfo.latitude) {
                enqueueSnackbar(Messages.GET_GEOLOCATION_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        }
        if (geolocationInfo.latitude && null != geolocationInfo.latitude) {
            try {
                const response = await ClientService.saveGeolocationData(
                    expedientId,
                    geolocationInfo
                );
                if (response.data && response.data.message) {
                    setGeolocationData(geolocationInfo);
                } else {
                    enqueueSnackbar(Messages.SAVE_GEOLOCATION_ERROR, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
            } catch (error) {
                enqueueSnackbar(Messages.SAVE_GEOLOCATION_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        }
    }

    async function handleDownload(doc: string) {
        setIsLoading(true);
        try {
            const response = await DocumentServices.getDocument(doc);
            Utils.functions.downloadDocs(response, `${doc}.pdf`);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                variant: SnackbarVariants.SUCCESS,
            });
        } catch (error) {
            enqueueSnackbar(Messages.FILE_DOWNLOAD_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
        } finally {
            setIsLoading(false);
        }
    }

    async function handleOtp(values: string) {
        setIsLoading(true);
        try {
            const response: EfeResponse<OtpValidation> =
                await ClientService.validateMailAuthentication(fatherKycId, values);
            if (response && response.data && response.data.authenticated) {
                setToken(!open);
                setOpen(!open);
                if (valuesToSubmit) {
                    updateData(valuesToSubmit);
                }
                enqueueSnackbar(Messages.OTP_AUTHORIZATION_Ok, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.OTP_UNAUTHORIZED, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.OTP_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }
    
    async function getOtp() {
        setIsLoading(true);
        try {
            const response: EfeResponse<MailAuthResponse> =
                await ClientService.getMailAuthentication(fatherKycId);
            if (response && response.data) {
                setOpen(!open);
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.OTP_AUTHORIZATION_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.OTP_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }
    
    async function updateData(values: ClientContactServiceRequest) {
        let message = messageFunctions.getAnalyticsMessage(
            AnalyticsCategories.USER,
            `${user.fullName}. error creating expedient.`
        );
        try {
            setIsLoading(true);
            const request = {...values};
            request.personTypeKey = personType;
            request.viewFatherId = flow.data.fatherId;
            request.viewId = flow.data.viewId;
            request.workflowId = 1;
            request.kycId = fatherKycId;
            request.expedientId = expedientId;
            request.personKey = user.username;
            const response: EfeResponse<ClientContactServiceRequest> =
                await ClientService.saveClientContact(request);
            if (response && response.data) {
                setExpedientId(response.data.expedientId);
                setKycId(response.data.kycId ? response.data.kycId : 1);
                setFatherKycId(response.data.kycId ? response.data.kycId : 0);
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                message = messageFunctions.getAnalyticsMessage(
                    AnalyticsCategories.USER,
                    `KycId: ${response.data.kycId}, PersonType: ${personType}, ExpedientId: ${response.data.expedientId}`
                );
                await saveGeolocation(response.data.expedientId);
                const path = nextStep(flow, menu, true);
                setIsLoading(false);
                history.push(path);
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
        sendEvent(AnalyticsCategories.KYC, message);
    }

    async function onSubmit(values: ClientContactServiceRequest) {
        if (!fatherKycId || fatherKycId === 0) {
            updateData(values);
        } else {
            setValuesToSubmit(values);
            getOtp();
        }
    }

    return (
        <div>
            <MainContent>
                <Box mt={4}>
                    <Typography variant='body2' color='textPrimary'>
                        {
                            'Con el fin de cumplir con las obligaciones de identificaci\u00F3n y conocimiento del Cliente contenidas en las Disposiciones de Car\u00E1cter General a que se refiere el art\u00EDculo 58 de la Ley para Regular las Instituciones de Tecnolog\u00EDa Financiera, es necesario que completes el siguiente expediente.'
                        }
                    </Typography>
                </Box>
                {showAlert && (
                    <EfeModalMsg
                        error={genericError}
                        open={showAlert}
                        defaultError={defaultError}
                        actions={actions}
                        handleAction={closeAlert}
                    />
                )}
                {useSpinner(isLoading)}
                {open && (
                    <EfeModal
                        open={open}
                        title={'Doble factor de autenticación'}
                        sizeOverride={30}
                        reactElement={
                            <EfeOtp
                                title='Introduce el código que acabamos de enviar a tu correo electrónico para poder guardar los cambios'
                                length={6}
                                isPassword={false}
                                handleClick={handleOtp}
                            />
                        }
                        handleAction={() => setOpen(!open)}
                    />
                )}
                <MuiLink
                    target='_blank'
                    href='https://www.dof.gob.mx/nota_detalle.php?codigo=5537449&fecha=10/09/2018#gsc.tab=0'
                    variant='body1'
                    color='primary'
                    underline='always'
                    rel={'noopener'}
                    className={classes.secondaryLink}>
                    Articulo 58
                </MuiLink>
                <MuiLink
                    className={classes.secondaryLink}
                    variant='body1'
                    color='primary'
                    underline='always'>
                    <Typography
                        onClick={() => handleDownload(Constants.DOC_PRIVACIDAD)}
                        component={'span'}
                        variant='body1'
                        className={classes.secondaryLink}>
                        Aviso de Privacidad
                    </Typography>
                </MuiLink>

                {!isLoading && (
                    <FrameworkForm
                        fields={fields}
                        title={flow.data.name}
                        isLoading={isLoading}
                        submitHandler={onSubmit}
                        enrollment={enrollment}
                        foreign={foreign}
                    />
                )}
            </MainContent>
        </div>
    );
}

export default RegulatoryFramework;
